import type { JSX } from 'react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Badge, Button, Dialog, VisuallyHidden } from '@artosera/ui';
import { estElevationAnnulee } from '../../components/useElevation.js';
import { annulerInvitationAssocie, modifierAssocie } from '../../lib/api.js';
import pageStyles from '../../styles/page.module.css';
import { TableauDefilant } from '../../components/TableauDefilant.js';
import tableStyles from '../../styles/table.module.css';
import saisie from '../Finances/TableauSaisie.module.css';
import { EtatOnglet, formaterDateHeure, useLangue, useMessageErreur } from '../Finances/commun.js';
import type { CompteAssocie } from '../../types.js';
import styles from './Associes.module.css';
import { useAssocies } from './useAssocies.js';

type Geste = <T>(geste: () => Promise<T>) => Promise<T>;

/**
 * Les comptes associés : nom, adresse, état de la 2FA, dernière connexion,
 * invitation en attente. Désactiver, réactiver, réinitialiser la 2FA et
 * annuler une invitation en attente exigent une vérification récente du
 * second facteur : `executer` ouvre la boîte d'élévation si l'API la
 * demande, puis rejoue le geste.
 */
export function ComptesAssocies({
  executer,
  enElevation,
}: {
  executer: Geste;
  /** La boîte d'élévation est ouverte : la confirmation s'efface, une seule boîte à la fois. */
  enElevation: boolean;
}): JSX.Element {
  const { t } = useTranslation('console');
  const langue = useLangue();
  const message = useMessageErreur();
  const { comptes, chargement, enErreur, relire } = useAssocies();
  const [erreurs, setErreurs] = useState<Record<string, string>>({});
  const [enCours, setEnCours] = useState<string | null>(null);
  const [aReinitialiser, setAReinitialiser] = useState<CompteAssocie | null>(null);
  const [aAnnuler, setAAnnuler] = useState<CompteAssocie | null>(null);
  const [aDesactiver, setADesactiver] = useState<CompteAssocie | null>(null);

  async function agir(compte: CompteAssocie, geste: () => Promise<void>): Promise<void> {
    setEnCours(compte.id);
    setErreurs(({ [compte.id]: _retiree, ...reste }) => reste);
    try {
      await executer(geste);
      await relire();
    } catch (erreur) {
      if (!estElevationAnnulee(erreur)) {
        setErreurs((courant) => ({ ...courant, [compte.id]: message(erreur) }));
      }
    } finally {
      setEnCours(null);
      setAReinitialiser(null);
      setAAnnuler(null);
      setADesactiver(null);
    }
  }

  const modifier = (
    compte: CompteAssocie,
    corps: { actif: boolean } | { reinitialiserDeuxFacteurs: true },
  ): Promise<void> => agir(compte, () => modifierAssocie(compte.id, corps));

  return (
    <section className={pageStyles.panneau} aria-labelledby="associes-comptes">
      <div className={pageStyles.sectionTitre}>
        <h2 id="associes-comptes">{t('associes.comptes.titre')}</h2>
      </div>

      {chargement || enErreur ? (
        <EtatOnglet chargement={chargement} enErreur={enErreur} />
      ) : (
        <TableauDefilant empile>
          <table
            className={`${tableStyles.table} ${saisie.empile}`}
            aria-label={t('associes.comptes.titre')}
          >
            <thead>
              <tr>
                <th scope="col">{t('associes.comptes.colonnes.nom')}</th>
                <th scope="col">{t('associes.comptes.colonnes.email')}</th>
                <th scope="col">{t('associes.comptes.colonnes.deuxFacteurs')}</th>
                <th scope="col">{t('associes.comptes.colonnes.derniereConnexion')}</th>
                <th scope="col">
                  <VisuallyHidden>{t('associes.comptes.colonnes.actions')}</VisuallyHidden>
                </th>
              </tr>
            </thead>
            <tbody>
              {comptes.length === 0 ? (
                <tr>
                  <td colSpan={5} className={saisie.videEtat}>
                    {t('associes.comptes.vide')}
                  </td>
                </tr>
              ) : null}
              {comptes.map((compte) => {
                // Une invitation en attente n'a pas encore de rôle : l'API refuse
                // `PATCH` (404) ; le seul geste offert est d'annuler l'invitation.
                const accepte = !compte.invitationEnAttente;
                return (
                  <ComptesLigne
                    key={compte.id}
                    compte={compte}
                    accepte={accepte}
                    enCours={enCours === compte.id}
                    erreur={erreurs[compte.id] ?? null}
                    derniereConnexion={
                      compte.derniereConnexionLe
                        ? formaterDateHeure(compte.derniereConnexionLe, langue)
                        : null
                    }
                    // Désactiver coupe l'accès d'une personne : on confirme d'abord.
                    // Réactiver ne détruit rien, il part tout de suite.
                    onBasculer={() =>
                      compte.actif ? setADesactiver(compte) : void modifier(compte, { actif: true })
                    }
                    onReinitialiser={() => setAReinitialiser(compte)}
                    onAnnulerInvitation={() => setAAnnuler(compte)}
                  />
                );
              })}
            </tbody>
          </table>
        </TableauDefilant>
      )}

      <Dialog
        open={aReinitialiser !== null && !enElevation}
        onOpenChange={(ouvert) => {
          if (!ouvert && enCours === null) setAReinitialiser(null);
        }}
        title={t('associes.reinitialisation.titre')}
        description={
          aReinitialiser
            ? t('associes.reinitialisation.texte', { nom: aReinitialiser.nom })
            : undefined
        }
        closeLabel={t('saisies.suppression.fermer')}
        footer={
          <>
            <Button
              variant="ghost"
              onClick={() => setAReinitialiser(null)}
              disabled={enCours !== null}
            >
              {t('saisies.annuler')}
            </Button>
            <Button
              variant="danger"
              loading={enCours !== null}
              onClick={() =>
                aReinitialiser && void modifier(aReinitialiser, { reinitialiserDeuxFacteurs: true })
              }
            >
              {enCours !== null
                ? t('associes.reinitialisation.enCours')
                : t('associes.reinitialisation.confirmer')}
            </Button>
          </>
        }
      />

      <Dialog
        open={aDesactiver !== null && !enElevation}
        onOpenChange={(ouvert) => {
          if (!ouvert && enCours === null) setADesactiver(null);
        }}
        title={t('associes.desactivation.titre')}
        description={
          aDesactiver ? t('associes.desactivation.texte', { nom: aDesactiver.nom }) : undefined
        }
        closeLabel={t('saisies.suppression.fermer')}
        footer={
          <>
            <Button
              variant="ghost"
              onClick={() => setADesactiver(null)}
              disabled={enCours !== null}
            >
              {t('associes.desactivation.garder')}
            </Button>
            <Button
              variant="danger"
              loading={enCours !== null}
              onClick={() => aDesactiver && void modifier(aDesactiver, { actif: false })}
            >
              {enCours !== null
                ? t('associes.desactivation.enCours')
                : t('associes.desactivation.confirmer')}
            </Button>
          </>
        }
      />

      <Dialog
        open={aAnnuler !== null && !enElevation}
        onOpenChange={(ouvert) => {
          if (!ouvert && enCours === null) setAAnnuler(null);
        }}
        title={t('associes.annulation.titre')}
        description={
          aAnnuler ? t('associes.annulation.texte', { email: aAnnuler.email }) : undefined
        }
        closeLabel={t('saisies.suppression.fermer')}
        footer={
          <>
            <Button variant="ghost" onClick={() => setAAnnuler(null)} disabled={enCours !== null}>
              {t('associes.annulation.garder')}
            </Button>
            <Button
              variant="danger"
              loading={enCours !== null}
              onClick={() => {
                if (!aAnnuler) return;
                const compte = aAnnuler;
                void agir(compte, () => annulerInvitationAssocie(compte.id));
              }}
            >
              {enCours !== null
                ? t('associes.annulation.enCours')
                : t('associes.annulation.confirmer')}
            </Button>
          </>
        }
      />
    </section>
  );
}

function ComptesLigne({
  compte,
  accepte,
  enCours,
  erreur,
  derniereConnexion,
  onBasculer,
  onReinitialiser,
  onAnnulerInvitation,
}: {
  compte: CompteAssocie;
  accepte: boolean;
  enCours: boolean;
  erreur: string | null;
  derniereConnexion: string | null;
  onBasculer: () => void;
  onReinitialiser: () => void;
  onAnnulerInvitation: () => void;
}): JSX.Element {
  const { t } = useTranslation('console');
  return (
    <>
      <tr>
        <td
          data-label={t('associes.comptes.colonnes.nom')}
          className={tableStyles.cellulePrincipale}
        >
          <span>{compte.nom}</span>
          {compte.invitationEnAttente || !compte.actif ? (
            <span className={styles.pastilles}>
              {compte.invitationEnAttente ? (
                <Badge tone="reserved">{t('associes.comptes.invitationEnAttente')}</Badge>
              ) : null}
              {!compte.actif ? <Badge tone="sold">{t('associes.comptes.desactive')}</Badge> : null}
            </span>
          ) : null}
        </td>
        <td data-label={t('associes.comptes.colonnes.email')} className={styles.courriel}>
          {compte.email}
        </td>
        <td data-label={t('associes.comptes.colonnes.deuxFacteurs')}>
          <Badge tone={compte.deuxiemeFacteurActive ? 'available' : 'reserved'}>
            {compte.deuxiemeFacteurActive
              ? t('associes.comptes.deuxFacteursActive')
              : t('associes.comptes.deuxFacteursInactive')}
          </Badge>
        </td>
        <td
          data-label={t('associes.comptes.colonnes.derniereConnexion')}
          className={
            derniereConnexion ? styles.mono : `${styles.mono} ${tableStyles.celluleMuette}`
          }
        >
          {derniereConnexion ?? t('associes.comptes.jamais')}
        </td>
        <td className={saisie.celluleActions}>
          <div className={saisie.actionsLigne}>
            {accepte ? (
              <>
                <Button
                  variant="secondary"
                  loading={enCours}
                  aria-label={t(
                    compte.actif
                      ? 'associes.comptes.desactiverLigne'
                      : 'associes.comptes.reactiverLigne',
                    { nom: compte.nom },
                  )}
                  onClick={onBasculer}
                >
                  {compte.actif
                    ? t('associes.comptes.desactiver')
                    : t('associes.comptes.reactiver')}
                </Button>
                <Button
                  variant="danger"
                  disabled={enCours || !compte.deuxiemeFacteurActive}
                  aria-label={t('associes.comptes.reinitialiserLigne', { nom: compte.nom })}
                  onClick={onReinitialiser}
                >
                  {t('associes.comptes.reinitialiser')}
                </Button>
              </>
            ) : (
              <>
                <span className={tableStyles.celluleMuette}>{t('associes.comptes.attente')}</span>
                <Button
                  variant="secondary"
                  loading={enCours}
                  aria-label={t('associes.comptes.annulerInvitationLigne', { nom: compte.nom })}
                  onClick={onAnnulerInvitation}
                >
                  {t('associes.comptes.annulerInvitation')}
                </Button>
              </>
            )}
          </div>
        </td>
      </tr>
      {erreur ? (
        <tr className={saisie.ligneErreur}>
          <td colSpan={5}>
            <p className={saisie.erreurLigne} role="alert">
              {erreur}
            </p>
          </td>
        </tr>
      ) : null}
    </>
  );
}
